import React, { useState } from "react";
import { Sparkles, Copy, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function SocialCoach() {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {raw, strategies: string[]}
  const [copiedIdx, setCopiedIdx] = useState(null);

  const generate = async () => {
    setLoading(true);
    setResult(null);
    setCopiedIdx(null);
    try {
      const r = await api.post("/agent/content-strategy", { input_utente: input.trim() });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella generazione");
    } finally {
      setLoading(false);
    }
  };

  const copy = (idx, text) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    toast.success("Strategia copiata");
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const strategies = result?.strategies || [];

  return (
    <div className="space-y-6 fade-slide max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold text-[#0A0A0A] flex items-center gap-2">
          <Sparkles size={26} className="text-[#B40101]" /> Social Coach
        </h1>
        <p className="text-gray-500 mt-1">
          Genera 3 strategie social settimanali pronte da usare oggi stesso.
        </p>
      </div>

      <div className="vg-card p-6 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">
            Cosa è successo questa settimana, dentro e fuori dai social?
          </label>
          <textarea
            className="vg-input min-h-[110px] resize-y leading-relaxed"
            placeholder="Es. ho fatto una visita interessante, ho ricevuto 2 lead, su Instagram pochi commenti questa settimana..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid="social-coach-input"
          />
          <p className="text-xs text-gray-400 mt-2">Opzionale. Se lasciato vuoto, vengono generate tre strategie valide comunque.</p>
        </div>

        <button
          className="vg-btn-primary px-6 inline-flex items-center gap-2"
          disabled={loading}
          onClick={generate}
          data-testid="social-coach-generate"
        >
          {loading ? <><Loader2 className="animate-spin" size={16} /> Genero…</> : <><Sparkles size={16} /> Genera 3 strategie</>}
        </button>
      </div>

      {result && (
        <div className="space-y-4" data-testid="social-coach-results">
          {strategies.length === 1 ? (
            <StrategyCard
              idx={0}
              title="Strategie generate"
              body={strategies[0]}
              copied={copiedIdx === 0}
              onCopy={() => copy(0, strategies[0])}
            />
          ) : (
            strategies.map((s, i) => (
              <StrategyCard
                key={i}
                idx={i}
                title={`Strategia ${i + 1}`}
                body={s}
                copied={copiedIdx === i}
                onCopy={() => copy(i, s)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function StrategyCard({ idx, title, body, copied, onCopy }) {
  return (
    <div className="vg-card p-6" data-testid={`strategy-card-${idx}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-[#0A0A0A] text-lg">{title}</h3>
        <button
          className="vg-btn-secondary inline-flex items-center gap-2 px-3 py-1.5 text-sm"
          onClick={onCopy}
          data-testid={`strategy-copy-${idx}`}
        >
          {copied ? <><Check size={14} /> Copiata</> : <><Copy size={14} /> Copia</>}
        </button>
      </div>
      <div
        className="text-sm text-[#0A0A0A] whitespace-pre-wrap leading-relaxed"
        data-testid={`strategy-body-${idx}`}
      >
        {body}
      </div>
    </div>
  );
}
